import type { ReactElement } from "react"
import React from "react"
import { getFormattedPeriodInfo } from "@core/period-info"
import { getCurrencyCode } from "@core/pricing"
import { PriceFormat } from "@core/types"
import { omitBy, isNil } from "lodash"
import { Helmet } from "react-helmet"
import { helmetJsonLdProp } from "react-schemaorg"
import type { Product, Offer, WithContext } from "schema-dts"
import type { ProductSchema as SquidexProductSchema } from "@gatsby-plugin-marketplace/fragments/marketplaceProduct"
import useSiteMetadataQuery from "~/hooks/useSiteMetadataQuery"
import isEmpty from "~/lib/isEmpty"

type ProductContext = WithContext<Product>

function createSchemaDefinition(
  {
    flatData: {
      name,
      description,
      sku,
      mpn,
      brand,
      availability,
      images,
      plan,
      gtin13,
      gtin14,
      gtin8,
    },
  }: SquidexProductSchema,
  url: string
): ProductContext {
  const offers: Offer = {
    "@type": "Offer",
    url,
    priceCurrency: getCurrencyCode(),
    price: getFormattedPeriodInfo(
      plan[0].flatData.periods,
      undefined,
      PriceFormat.REGISTER_GROSS_DOT_SEPARATED
    ),
  }

  if (availability) {
    offers["availability"] = availability
  }

  const product: ProductContext = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    sku,
    mpn,
    gtin13,
    gtin14,
    gtin8,
    offers,
  }

  if (images && images.length) {
    product.image = images.map((item) => item.url)
  }

  if (brand) {
    product.brand = {
      "@type": "Brand",
      name: brand,
    }
  }

  return omitBy(product, isNil) as ProductContext
}

interface ProductSchemaProps {
  items: SquidexProductSchema[]
  url: string
}

export default function ProductSchema({
  items,
  url,
}: ProductSchemaProps): ReactElement<ProductSchemaProps> | null {
  const site = useSiteMetadataQuery()

  if (isEmpty(items)) {
    return null
  }

  return (
    <Helmet
      script={items
        .filter(
          (schema: SquidexProductSchema) => !isEmpty(schema.flatData.plan)
        )
        .map((schema: SquidexProductSchema) =>
          helmetJsonLdProp(
            createSchemaDefinition(schema, `${site.siteUrl}${url}`)
          )
        )}
    />
  )
}
